import { takeLatest, put, call, takeEvery, cancelled } from "redux-saga/effects";
import { toast } from "react-toastify";

//Redux Actions
import CONSTANTS from "../CONSTANTS";
import KitsService from "services/KitsService";
import { 
  actionSetEditKit, 
  actionSetKitsList, 
  actionSetKitTypes,
  actionSetKitsCount,
  actionSetPatientKits,
  actionSetKitStateMachine,
  actionSetKitStateGroups,
  actionSetKitStateList,
  actionSetKitsStateCount,
  actionGetKit,
  actionGetPatientKits,
  actionGetKitsList
} from "../actions/kitsActions";
import historyRouter from 'historyRouter';

function* sagaGetKit({ kitId }) {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.getKit, kitId, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Get kit failed');
    }
    yield put(actionSetEditKit(result.data));
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Get kit failed');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaGetKitsList({ page, limit, search, state }) {
  const cancelToken = KitsService.getSource();

  try {
    const result = yield call(KitsService.getKitsList, page, limit, state, search, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Get kits failed');
    }
    yield put(actionSetKitsList(result.data));
  } catch (error) {
    toast.error(error.message || 'Get kits failed');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaKitSubmitForm({ data }) {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.createKit, data, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Create kits failed');
    }
    toast.success('Kit created successfully');
    yield put(actionSetEditKit(result.data));
    historyRouter.replace(`admin/kits/form/${result.data._id}`);
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Submit kit failed');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaKitSubmitEdition({ data }) {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.editKit, data, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Edit kits failed');
    }
    toast.success('Kit edited successfully');
    yield put(actionSetEditKit({}));
    historyRouter.goBack();
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Edit kit failed');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaKitDelete({ kitId }) {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.deleteKit, kitId, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Delete kits failed');
    }
    toast.success('Kit deleted successfully');
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Delete kit failed');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaGetKitTypes() {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.getTypes, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error getting kit types');
    }
    yield put(actionSetKitTypes(result.data));
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Error getting kit types');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaGetKitStateMachine() {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.getKitStateMachine, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error getting kit state machine');
    }
    yield put(actionSetKitStateMachine(result.data));
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Error getting kit state machine');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

// #2121 - Grupos del stepper.
function* sagaGetKitStateGroups() {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.getKitStateGroups, {cancelToken: cancelToken.token});
    if (result.status >= 400) throw new Error('Error getting kit state groups');
    yield put(actionSetKitStateGroups(result.data));
  } catch (e) {
    console.log(e);
  } finally {
    if (yield cancelled()) cancelToken.cancel();
  }
}

function* sagaGetKitStateList() {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.getKitStateList, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error getting kit states');
    }
    yield put(actionSetKitStateList(result.data));
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Error getting kit states');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaGetKitCountByTypes({ practiceId } = {}) {
  const cancelToken = KitsService.getSource();
  try {
    // #2121-spec-kits — practiceId opcional
    const result = yield call(KitsService.getCountByTypes, practiceId || null, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error getting kits count');
    }
    yield put(actionSetKitsCount(result.data));
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Error getting kits count');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaGetPatientKits({ page, limit, state, search }) {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.getPatientKits, page, limit, state, search, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error getting patient kits');
    }
    yield put(actionSetPatientKits(result.data));
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Error getting patient kits');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaUpdatePatientKitStatus({ kitId, status }) {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.updatePatientKistStatus, kitId, status, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error updating patient kit');
    }
    yield put(actionSetEditKit(result.data));
    toast.success("Kit state updated successfully");
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Error updating patient kit');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaGetKitsCountByState({ practiceId } = {}) {
  const cancelToken = KitsService.getSource();
  try {
    // #2121-spec-kits — practiceId opcional
    const result = yield call(KitsService.getKitsCountByState, practiceId || null, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error getting kits count');
    }
    yield put(actionSetKitsStateCount(result.data));
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Error getting kits count');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaSubmitKitObservation({ kitId, obv }) {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.saveKitObservations, kitId, obv, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error saving the observations');
    }
    toast.success("Observations saved");
    yield put(actionGetKit(kitId));
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Error saving the observations');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaClearKit({ kitId, page, limit, activeTab, currentFilter }) {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.clearKit, kitId, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error clearing the kit');
    }
    toast.success("Kit cleared");
    yield put(actionGetPatientKits(page, limit, activeTab, currentFilter));
    yield put(actionGetKitsList(page, limit, activeTab, currentFilter));
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Error clearing the kit');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}

function* sagaSaveKitTrackingId({ kitId, trackingId }) {
  const cancelToken = KitsService.getSource();
  try {
    const result = yield call(KitsService.saveTrackingId, kitId, trackingId, {cancelToken: cancelToken.token});
    if (result.status >= 400) {
      throw new Error('Error saving the kit tracking ID');
    }
    yield put(actionGetKit(kitId));
  } catch (e) {
    console.log(e);
    toast.error(e.message || 'Error saving the tracking ID');
  } finally {
    if (yield cancelled()) {
      cancelToken.cancel();
    }
  }
}


export function* kitsSaga() {
  console.log("*Main Kits saga");
  yield takeLatest(CONSTANTS.KITS_GET, sagaGetKit);
  yield takeLatest(CONSTANTS.KITS_GET_LIST, sagaGetKitsList);
  yield takeLatest(CONSTANTS.KITS_SUBMIT_FORM, sagaKitSubmitForm);
  yield takeLatest(CONSTANTS.KITS_SUBMIT_EDIT, sagaKitSubmitEdition);
  yield takeLatest(CONSTANTS.KIT_DELETE, sagaKitDelete);
  yield takeLatest(CONSTANTS.KITS_GET_TYPES, sagaGetKitTypes);
  yield takeLatest(CONSTANTS.KITS_GET_STATE_LIST, sagaGetKitStateList);
  yield takeLatest(CONSTANTS.KITS_GET_STATE_MACHINE, sagaGetKitStateMachine);
  yield takeLatest(CONSTANTS.KITS_GET_STATE_GROUPS, sagaGetKitStateGroups);
  yield takeLatest(CONSTANTS.KITS_GET_COUNT_BY_TYPE, sagaGetKitCountByTypes);
  yield takeLatest(CONSTANTS.KITS_PATIENTS_GET, sagaGetPatientKits);
  yield takeLatest(CONSTANTS.KIT_PATIENTS_UPDATE_STATUS, sagaUpdatePatientKitStatus);
  yield takeLatest(CONSTANTS.KITS_GET_COUNT_BY_STATE, sagaGetKitsCountByState);
  yield takeLatest(CONSTANTS.KITS_SAVE_OBSERVATION, sagaSubmitKitObservation);
  yield takeLatest(CONSTANTS.KITS_CLEAR_KIT, sagaClearKit);
  yield takeLatest(CONSTANTS.KITS_SAVE_TRACKING_ID, sagaSaveKitTrackingId);
}
  